import { Suspense, lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { useMe } from "./lib/api";
import { DAILY_TAB, MASTER_TAB, WEEKLY_TAB } from "./lib/sheet";
import { SessionProvider, useSession } from "./lib/session";
import { AppLayout } from "./components/AppLayout";
import { Card } from "./components/Card";
import { ErrorState } from "./components/ErrorState";
import { RoleGuard } from "./components/RoleGuard";
import { LoadingBlock, Spinner } from "./components/Spinner";
import { InputPage } from "./pages/InputPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { TabTablePage } from "./pages/TabTablePage";

/** ECharts is heavy; keep it out of the initial bundle. */
const DashboardPage = lazy(() =>
  import("./pages/DashboardPage").then((module) => ({ default: module.DashboardPage })),
);

/** Stable identities so TabTablePage's memo never churns on prop literals. */
const MASTER_ONLY = [MASTER_TAB];
const DAILY_ONLY = [DAILY_TAB];
const WEEKLY_ONLY = [WEEKLY_TAB];
const TIM_EXCLUDE = [MASTER_TAB, DAILY_TAB, WEEKLY_TAB];

function BootScreen() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-bg text-muted">
      <Spinner size="lg" label="Memeriksa sesi" />
      <p className="text-sm">Memeriksa sesi Anda…</p>
    </div>
  );
}

function HomeRedirect() {
  const me = useSession();
  if (!me) {
    return null;
  }
  return <Navigate to={me.role === "admin" ? "/admin/dashboard" : "/input"} replace />;
}

function AdminIndexRedirect() {
  return <Navigate to="/admin/dashboard" replace />;
}

export default function App() {
  const me = useMe();

  if (me.loading) {
    return <BootScreen />;
  }

  if (me.error || !me.data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg p-6">
        <Card className="w-full max-w-lg">
          <ErrorState
            error={me.error}
            title="Tidak dapat memuat sesi"
            onRetry={me.reload}
          />
        </Card>
      </div>
    );
  }

  return (
    <SessionProvider value={me.data}>
      <Routes>
        <Route element={<AppLayout />}>
          <Route index element={<HomeRedirect />} />
          <Route
            path="/input"
            element={
              <RoleGuard allow="leader">
                <InputPage />
              </RoleGuard>
            }
          />
          <Route
            path="/admin"
            element={
              <RoleGuard allow="admin">
                <AdminIndexRedirect />
              </RoleGuard>
            }
          />
          <Route
            path="/admin/dashboard"
            element={
              <RoleGuard allow="admin">
                <Suspense
                  fallback={
                    <Card>
                      <LoadingBlock message="Menyiapkan dashboard…" />
                    </Card>
                  }
                >
                  <DashboardPage />
                </Suspense>
              </RoleGuard>
            }
          />
          <Route
            path="/admin/master"
            element={
              <RoleGuard allow="admin">
                <TabTablePage
                  only={MASTER_ONLY}
                  emptyTitle="Tab master tidak ditemukan"
                  emptyDescription='Tidak ditemukan tab "MASTER DATA" pada spreadsheet.'
                />
              </RoleGuard>
            }
          />
          <Route
            path="/admin/harian"
            element={
              <RoleGuard allow="admin">
                <TabTablePage
                  only={DAILY_ONLY}
                  emptyTitle="Tab harian tidak ditemukan"
                  emptyDescription='Tidak ditemukan tab "HARIAN" pada spreadsheet.'
                />
              </RoleGuard>
            }
          />
          <Route
            path="/admin/mingguan"
            element={
              <RoleGuard allow="admin">
                <TabTablePage
                  only={WEEKLY_ONLY}
                  emptyTitle="Tab mingguan tidak ditemukan"
                  emptyDescription='Tidak ditemukan tab "MINGUAN" pada spreadsheet.'
                />
              </RoleGuard>
            }
          />
          <Route
            path="/admin/tim"
            element={
              <RoleGuard allow="admin">
                <TabTablePage
                  exclude={TIM_EXCLUDE}
                  emptyTitle="Belum ada tab tim"
                  emptyDescription="Tidak ditemukan tab lain selain MASTER DATA, HARIAN, dan MINGUAN."
                />
              </RoleGuard>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </SessionProvider>
  );
}
